import React, { useState, useEffect } from "react";
import { X, Search, ChevronLeft, ChevronRight, Trophy, Calendar, Tag, Banknote, Users, Star, Award as AwardIcon, CheckCircle2, XCircle, Plus, Edit2,Eye, Sparkles, FileText, Link } from "lucide-react";
import api from "../../../services/api";


import FilterSidebar from "./FilterSidebar";
import DetailedAccordion from "./DetailedAccordion";
import ActionButtons from "./ActionButton";
import { BASE_URL } from "../../../services/configUrls";
import CompactCategorySelector from "./AwardCategories";
import AddAwardModal from "./AddAwardModal";
import AwardQuestionsModal from "./AwardQuestionsModal";
import NomineesDashboard from "./NomineesDashboard";

// Leaderwise Votes Modal Component
const LeaderwiseVotesModal = ({ isOpen, onClose, apiCall }) => {
  const [leaderwiseData, setLeaderwiseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("leaders");
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    designation: "",
    institute: "",
  });

  useEffect(() => {
    if (isOpen) {
      fetchLeaderwiseData();
    }
  }, [isOpen]);

  const fetchLeaderwiseData = async () => {
    try {
      setLoading(true);
      const data = await apiCall("/admin/leaderwise-votes-summary");
      setLeaderwiseData(data);
    } catch (error) {
      console.error("Error fetching leaderwise data:", error);
      alert("Failed to fetch leaderwise votes summary.");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentData = () => {
    if (!leaderwiseData) return [];
    return activeTab === "leaders"
      ? leaderwiseData.leader_votes_summary || []
      : leaderwiseData.tpo_votes_summary || [];
  };

  // Get unique values for filters
  const getUniqueValues = (key) => {
    const data = getCurrentData();
    const values = data
      .map((item) => {
        if (key === "designation") {
          return item.designation?.replace(/_/g, " ");
        }
        return item[key];
      })
      .filter(Boolean);
    return [...new Set(values)].sort();
  };

  const filteredData = getCurrentData().filter((item) => {
    const matchesSearch =
      item.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.email &&
        item.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.institute_name &&
        item.institute_name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesDesignation =
      !filters.designation ||
      item.designation?.replace(/_/g, " ").toLowerCase() ===
      filters.designation.toLowerCase();

    const matchesInstitute =
      !filters.institute ||
      item.institute_name?.toLowerCase() === filters.institute.toLowerCase();

    return matchesSearch && matchesDesignation && matchesInstitute;
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm("");
    setFilters({ designation: "", institute: "" });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilters({ designation: "", institute: "" });
    setCurrentPage(1);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 9998 }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        style={{ zIndex: 9998 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b shrink-0">
          <h2 className="text-2xl font-semibold text-gray-800">
            Votes Summary
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-pulse text-gray-500">Loading...</div>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => handleTabChange("leaders")}
                  className={`flex-1 py-2 px-4 rounded-md transition-colors ${activeTab === "leaders"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                    }`}
                >
                  Leader Votes (
                  {leaderwiseData?.leader_votes_summary?.length || 0})
                </button>
                <button
                  onClick={() => handleTabChange("tpo")}
                  className={`flex-1 py-2 px-4 rounded-md transition-colors ${activeTab === "tpo"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                    }`}
                >
                  TPO Votes ({leaderwiseData?.tpo_votes_summary?.length || 0})
                </button>
              </div>

              {/* Search Bar and Filters */}
              <div className="flex flex-wrap gap-3 mb-4 items-end">
                {/* Search Bar */}
                <div className="relative flex-1 min-w-[280px] max-w-[400px]">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={16}
                  />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* Designation Filter */}
                <div className="min-w-[140px]">
                  <select
                    value={filters.designation}
                    onChange={(e) =>
                      handleFilterChange("designation", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">All Designations</option>
                    {getUniqueValues("designation").map((designation) => (
                      <option key={designation} value={designation}>
                        {designation}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Institute Filter */}
                <div className="min-w-[140px]">
                  <select
                    value={filters.institute}
                    onChange={(e) =>
                      handleFilterChange("institute", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">All Institutes</option>
                    {getUniqueValues("institute_name").map((institute) => (
                      <option key={institute} value={institute}>
                        {institute}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Clear Filters Button */}
                <div>
                  <button
                    onClick={clearAllFilters}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                  >
                    Clear
                  </button>
                </div>

                {/* Items Per Page Selector */}
                <div className="min-w-[80px]">
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    className="w-full px-2 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value={5}>5 rows</option>
                    <option value={10}>10 rows</option>
                    <option value={50}>50 rows</option>
                  </select>
                </div>
              </div>

              {/* Active Filters Display */}
              {(filters.designation || filters.institute || searchTerm) && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {searchTerm && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Search: "{searchTerm}"
                      <button
                        onClick={() => setSearchTerm("")}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {filters.designation && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Designation: {filters.designation}
                      <button
                        onClick={() => handleFilterChange("designation", "")}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {filters.institute && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Institute: {filters.institute}
                      <button
                        onClick={() => handleFilterChange("institute", "")}
                        className="ml-2 text-purple-600 hover:text-purple-800"
                      >
                        ×
                      </button>
                    </span>
                  )}
                </div>
              )}

              {/* Table Container with Fixed Height and Scroll */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div
                  style={{
                    height:
                      itemsPerPage === 5
                        ? "300px"
                        : itemsPerPage === 10
                          ? "500px"
                          : "600px",
                    overflow: "auto",
                    border: "1px solid transparent",
                  }}
                >
                  <div
                    style={{
                      minHeight:
                        itemsPerPage === 5
                          ? "301px"
                          : itemsPerPage === 10
                            ? "501px"
                            : "601px",
                      overflowY: "scroll",
                      height: "100%",
                    }}
                  >
                    <table
                      className="w-full border-collapse"
                      style={{ minHeight: "100%" }}
                    >
                      <thead className="sticky top-0 bg-gray-50 z-10">
                        <tr>
                          <th className="text-left p-3 border-b font-semibold text-gray-700 text-sm">
                            Name
                          </th>
                          <th className="text-left p-3 border-b font-semibold text-gray-700 text-sm">
                            Email
                          </th>
                          <th className="text-left p-3 border-b font-semibold text-gray-700 text-sm">
                            Institute
                          </th>
                          <th className="text-left p-3 border-b font-semibold text-gray-700 text-sm">
                            Designation
                          </th>
                          <th className="text-left p-3 border-b font-semibold text-gray-700 text-sm">
                            Category
                          </th>
                          <th className="text-center p-3 border-b font-semibold text-gray-700 text-sm">
                            Student Votes
                          </th>
                          {activeTab === "leaders" ? (
                            <th className="text-center p-3 border-b font-semibold text-gray-700 text-sm">
                              Total Score
                            </th>
                          ) : (
                            <>
                              <th className="text-center p-3 border-b font-semibold text-gray-700 text-sm">
                                Corporate Votes
                              </th>
                              <th className="text-center p-3 border-b font-semibold text-gray-700 text-sm">
                                Score
                              </th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {paginatedData.length > 0 ? (
                          paginatedData.map((item, index) => (
                            <tr
                              key={index}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <td className="p-3 border-b font-medium text-gray-800 text-sm">
                                {item.full_name}
                              </td>
                              <td className="p-3 border-b text-gray-600 text-sm">
                                {item.email || "N/A"}
                              </td>
                              <td className="p-3 border-b text-gray-600 text-sm">
                                {item.institute_name || "N/A"}
                              </td>
                              <td className="p-3 border-b text-gray-600 capitalize text-sm">
                                {item.designation.replace(/_/g, " ")}
                              </td>
                              <td className="p-3 border-b text-gray-600 text-sm">
                                {item.category_name}
                              </td>
                              <td className="p-3 border-b text-center">
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                                  {item.student_votes}
                                </span>
                              </td>
                              {activeTab === "leaders" ? (
                                <td className="p-3 border-b text-center">
                                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                                    {item.total_score}
                                  </span>
                                </td>
                              ) : (
                                <>
                                  <td className="p-3 border-b text-center">
                                    <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                                      {item.corporate_votes}
                                    </span>
                                  </td>
                                  <td className="p-3 border-b text-center">
                                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                                      {item.total_score}
                                    </span>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={activeTab === "leaders" ? 7 : 8}
                              className="p-8 text-center text-gray-500"
                            >
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {/* Force scroll by adding extra content */}
                    <div style={{ height: "1px", width: "100%" }}></div>
                  </div>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1} to{" "}
                    {Math.min(startIndex + itemsPerPage, filteredData.length)}{" "}
                    of {filteredData.length} entries
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    <div className="flex space-x-1">
                      {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 rounded-lg text-sm transition-colors ${currentPage === pageNum
                              ? "bg-blue-500 text-white"
                              : "text-gray-600 hover:bg-gray-100"
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Vote Summary Component
const VoteSummary = ({ voteSummary, loading, apiCall }) => {
  const [showLeaderwiseModal, setShowLeaderwiseModal] = useState(false);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!voteSummary) return null;

  const {
    total_leader_nominations = 0,
    total_tpo_nominations = 0,
    total_student_votes = 0,
    total_corporate_votes = 0,
    leader_nominations_designationwise = {},
  } = voteSummary;

  return (
    <>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Vote & Nomination Summary
        </h2>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
            <div className="text-2xl font-bold">{total_leader_nominations}</div>
            <div className="text-sm opacity-90">Leader Nominations</div>
          </div>

          {/* <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
            <div className="text-2xl font-bold">{total_tpo_nominations}</div>
            <div className="text-sm opacity-90">TPO Nominations</div>
          </div> */}

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
            <div className="text-2xl font-bold">{total_student_votes}</div>
            <div className="text-sm opacity-90">Student Votes</div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-4 text-white">
            <div className="text-2xl font-bold">{total_corporate_votes}</div>
            <div className="text-sm opacity-90">Corporate Votes</div>
          </div>

          {/* New Leaderwise Votes Card */}
          <div
            className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg p-4 text-white cursor-pointer hover:from-indigo-600 hover:to-indigo-700 transition-colors transform hover:scale-105"
            onClick={() => setShowLeaderwiseModal(true)}
          >
            <div className="text-2xl font-bold">📊</div>
            <div className="text-sm opacity-90">Leaderwise Votes</div>
            <div className="text-xs opacity-75 mt-1">Click to view details</div>
          </div>
        </div>

        {/* Leader Nominations by Designation */}
        {Object.keys(leader_nominations_designationwise).length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-3">
              Leader Nominations by Designation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Object.entries(leader_nominations_designationwise).map(
                ([designation, count]) => (
                  <div
                    key={designation}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between"
                  >
                    <div className="text-sm font-medium text-gray-700 capitalize">
                      {designation.replace(/_/g, " ")}
                    </div>
                    <div className="bg-indigo-100 text-indigo-800 text-sm font-semibold px-2 py-1 rounded-full">
                      {count}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>

      {/* Leaderwise Votes Modal */}
      <LeaderwiseVotesModal
        isOpen={showLeaderwiseModal}
        onClose={() => setShowLeaderwiseModal(false)}
        apiCall={apiCall}
      />
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Admin: 1 Million Internship Milestone Submissions Manager
// ─────────────────────────────────────────────────────────────────────────────
const MilestoneSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [viewLoadingKey, setViewLoadingKey] = useState(null);
  const [viewModalUrl, setViewModalUrl] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const searchTimer = React.useRef(null);

  const getToken = () => localStorage.getItem("accessToken");

  const fetchSubmissions = async (pg = page, status = filterStatus, institute = searchTerm, ps = pageSize) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ page: pg, page_size: ps });
      if (status) params.append("status", status);
      if (institute.trim()) params.append("institute_name", institute.trim());
      const res = await api.get(`${BASE_URL}/awards/get-submissions?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = res.data;
      setSubmissions(data.data || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when status filter changes
  useEffect(() => { fetchSubmissions(1, filterStatus, searchTerm, pageSize); setPage(1); }, [filterStatus]);


  // Debounced search
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchSubmissions(1, filterStatus, val, pageSize);
    }, 400);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    clearTimeout(searchTimer.current);
    setPage(1);
    fetchSubmissions(1, filterStatus, "", pageSize);
  };


  const handleViewImage = async (s3Key) => {
    try {
      setViewLoadingKey(s3Key);
      const res = await api.get(`${BASE_URL}/awards/image-url?s3_key=${encodeURIComponent(s3Key)}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setViewModalUrl(res.data.url);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load image.");
    } finally {
      setViewLoadingKey(null);
    }
  };

  const handleIssue = async (submissionId) => {
    setConfirmModal({
      title: "Approve Submission",
      message: "Are you sure you want to approve these? This will issue a certificate.",
      confirmText: "Approve",
      action: async () => {
        setConfirmModal(null);
        try {
          setActionLoadingId(submissionId);
          setError(null);
          await api.post(`${BASE_URL}/awards/submissions/${submissionId}/issue`, {}, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          setSuccessMsg("Certificate issued successfully!");
          await fetchSubmissions(page, filterStatus, searchTerm, pageSize);
        } catch (err) {
          setError(err.response?.data?.detail || "Failed to issue certificate.");
        } finally {
          setActionLoadingId(null);
        }
      }
    });
  };

  const handleOpenReject = (submissionId) => {
    setRejectReason("");
    setRejectModal({ submission_id: submissionId });
  };

  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) return;
    try {
      setActionLoadingId(rejectModal.submission_id);
      setError(null);
      await api.post(`${BASE_URL}/awards/submissions/${rejectModal.submission_id}/reject`,
        { reason: rejectReason.trim() },
        { headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" } }
      );
      setSuccessMsg("Submission rejected.");
      setRejectModal(null);
      setRejectReason("");
      await fetchSubmissions(page, filterStatus, searchTerm);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reject submission.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const statusBadge = (status) => {
    const cfg = {
      pending: { cls: "bg-amber-50 text-amber-700 border border-amber-200", icon: "⏳", label: "Pending" },
      approved: { cls: "bg-emerald-50 text-emerald-700 border border-emerald-200", icon: "✅", label: "Approved" },
      rejected: { cls: "bg-red-50 text-red-700 border border-red-200", icon: "❌", label: "Rejected" },
    };
    const c = cfg[(status || "").toLowerCase()] || { cls: "bg-gray-100 text-gray-600 border border-gray-200", icon: "•", label: status };
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${c.cls}`}>
        {c.icon} {c.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Banner */}
      <div className="flex items-center gap-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl px-6 py-4">
        <div className="p-2 bg-indigo-100 rounded-xl shrink-0">
          <Sparkles className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h3 className="font-bold text-indigo-800 text-base">1 Million Internship Milestone — Submissions</h3>
          <p className="text-indigo-600 text-xs mt-0.5">Review institute screenshot submissions. Issue or reject certificates.</p>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by institute name..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-400 focus:border-transparent outline-none transition-all placeholder:text-gray-400"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-400 outline-none min-w-[130px]"
          >
            <option value="">All Statuses</option>
            <option value="pending">⏳ Pending</option>
            <option value="approved">✅ Approved</option>
            <option value="rejected">❌ Rejected</option>
          </select>

        </div>

        {/* Active filter pills */}
        {(searchTerm || filterStatus) && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-500 font-medium">Filters:</span>
            {searchTerm && (
              <span className="inline-flex items-center gap-1.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 rounded-full font-medium">
                Institute: "{searchTerm}"
                <button onClick={handleClearSearch} className="text-indigo-400 hover:text-indigo-600"><X className="w-3 h-3" /></button>
              </span>
            )}
            {filterStatus && (
              <span className="inline-flex items-center gap-1.5 text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-1 rounded-full font-medium capitalize">
                Status: {filterStatus}
                <button onClick={() => setFilterStatus("")} className="text-purple-400 hover:text-purple-600"><X className="w-3 h-3" /></button>
              </span>
            )}
          </div>
        )}
      </div>


      {/* Alerts */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <span className="text-red-500 text-lg leading-none mt-0.5">⚠️</span>
          <p className="text-red-700 text-sm font-medium flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      )}
      {successMsg && (
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <span className="text-emerald-500 text-lg leading-none mt-0.5">✅</span>
          <p className="text-emerald-700 text-sm font-medium flex-1">{successMsg}</p>
          <button onClick={() => setSuccessMsg(null)} className="text-emerald-400 hover:text-emerald-600"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500 text-sm">Loading submissions...</p>
          </div>
        </div>
      ) : submissions.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center shadow-sm">
          <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-indigo-400" />
          </div>
          <h4 className="font-bold text-gray-700 mb-2">No Submissions Found</h4>
          <p className="text-gray-500 text-sm">{filterStatus ? `No "${filterStatus}" submissions.` : "No institutes have submitted screenshots yet."}</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 font-medium">{total} total submission{total !== 1 ? "s" : ""}</p>
          {submissions.map((sub) => {
            const st = (sub.status || "").toLowerCase();
            return (
              <div
                key={sub.submission_id}
                className={`bg-white rounded-xl border shadow-sm overflow-hidden ${st === "rejected" ? "border-red-200" :
                  st === "approved" ? "border-emerald-200" : "border-gray-200"
                  }`}
              >
                {/* Compact single-row header */}
                <div className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b ${st === "rejected" ? "bg-red-50/60 border-red-100" :
                  st === "approved" ? "bg-emerald-50/60 border-emerald-100" :
                    "bg-gray-50 border-gray-100"
                  }`}>
                  {/* Left: meta */}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded shrink-0">#{sub.submission_id}</span>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-800 text-xs leading-tight truncate">{sub.award_name}</p>
                      <p className="text-[10px] text-gray-400 leading-tight truncate">
                        {sub.institute_name && <span className="font-semibold text-indigo-600">{sub.institute_name}</span>}
                        {sub.institute_name && sub.uploaded_by && <span className="mx-1">&middot;</span>}
                        {sub.uploaded_by && <span>{sub.uploaded_by}</span>}
                      </p>
                    </div>
                  </div>
                  {/* Right: status + actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {statusBadge(sub.status)}
                    {/* Issue / Reject inline for pending */}
                    {st === "pending" && (
                      <>
                        <button
                          onClick={() => handleIssue(sub.submission_id)}
                          disabled={actionLoadingId === sub.submission_id}
                          title="Issue Certificate"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[11px] font-bold rounded-lg transition-colors"
                        >
                          {actionLoadingId === sub.submission_id
                            ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <Trophy className="w-3 h-3" />}
                          Issue
                        </button>
                        <button
                          onClick={() => handleOpenReject(sub.submission_id)}
                          disabled={actionLoadingId === sub.submission_id}
                          title="Reject"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-[11px] font-bold rounded-lg transition-colors"
                        >
                          <X className="w-3 h-3" /> Reject
                        </button>
                      </>
                    )}
                    {st === "approved" && (
                      <span className="text-[10px] font-semibold text-emerald-700">Certificate issued</span>
                    )}
                  </div>
                </div>

                <div className="px-4 py-2.5 space-y-2">
                  {/* Share Links as horizontal chips */}
                  {sub.media_links && sub.media_links.length > 0 && (
                    <div className="flex flex-col sm:flex-row flex-wrap gap-1.5 pb-1">
                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mr-1 sm:mt-1.5">Share Links:</span>
                      {(Array.isArray(sub.media_links) ? sub.media_links : typeof sub.media_links === "string" ? sub.media_links.split(",") : []).map((link, idx) => (
                        <a
                          key={idx}
                          href={link.trim().startsWith("http") ? link.trim() : `https://${link.trim()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                          title={link.trim()}
                        >
                          <Link className="w-3 h-3 text-indigo-500 shrink-0" />
                          <span className="truncate max-w-[250px]">{link.trim()}</span>
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Images as horizontal chips */}
                  {sub.images && sub.images.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {sub.images.map((img, idx) => (
                        <button
                          key={img.image_id}
                          onClick={() => handleViewImage(img.s3_key)}
                          disabled={viewLoadingKey === img.s3_key}
                          className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 disabled:opacity-60 transition-colors"
                        >
                          {viewLoadingKey === img.s3_key
                            ? <span className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                            : <Eye className="w-3 h-3" />}
                          Image {idx + 1}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Rejection reason */}
                  {st === "rejected" && (
                    <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-1.5 mt-2">
                      <span className="font-bold">Reason:</span> {sub.reason || "No reason provided."}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages >= 1 && (
        <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  const ps = Number(e.target.value);
                  setPageSize(ps);
                  setPage(1);
                  fetchSubmissions(1, filterStatus, searchTerm, ps);
                }}
                className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 bg-white focus:ring-2 focus:ring-indigo-400 outline-none transition-all shadow-2xs cursor-pointer"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <span className="text-gray-300 hidden sm:inline">|</span>
            <p className="text-sm text-gray-500 font-medium">
              Showing&nbsp;
              <span className="font-bold text-gray-800">{total === 0 ? 0 : Math.min((page - 1) * pageSize + 1, total)}</span>
              &ndash;
              <span className="font-bold text-gray-800">{Math.min(page * pageSize, total)}</span>
              &nbsp;of&nbsp;
              <span className="font-bold text-gray-800">{total}</span> submissions
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Prev */}
            <button
              onClick={() => { const p = Math.max(page - 1, 1); setPage(p); fetchSubmissions(p, filterStatus, searchTerm, pageSize); }}
              disabled={page === 1}
              className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={15} />
            </button>

            {/* Page number buttons */}
            {[...Array(totalPages)].map((_, i) => {
              const pageNum = i + 1;
              const isActive = pageNum === page;
              const show = pageNum === 1 || pageNum === totalPages || Math.abs(page - pageNum) <= 1;
              const showDotsBefore = pageNum === page - 2 && pageNum > 1;
              const showDotsAfter = pageNum === page + 2 && pageNum < totalPages;
              if (showDotsBefore || showDotsAfter) {
                return <span key={`dots-${pageNum}`} className="px-1 text-gray-400 text-sm select-none">…</span>;
              }
              if (!show) return null;
              return (
                <button
                  key={pageNum}
                  onClick={() => { setPage(pageNum); fetchSubmissions(pageNum, filterStatus, searchTerm, pageSize); }}
                  className={`min-w-[34px] h-[34px] px-2 rounded-lg text-sm font-bold transition-colors ${isActive
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-gray-600 border border-gray-200 hover:bg-gray-100"
                    }`}
                >
                  {pageNum}
                </button>
              );
            })}

            {/* Next */}
            <button
              onClick={() => { const p = Math.min(page + 1, totalPages); setPage(p); fetchSubmissions(p, filterStatus, searchTerm, pageSize); }}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* Image View Modal */}
      {viewModalUrl && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 60000 }}
          onClick={() => setViewModalUrl(null)}
        >
          <div
            className="relative bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50 shrink-0">
              <p className="text-sm font-semibold text-gray-700">Submission Screenshot</p>
              <button onClick={() => setViewModalUrl(null)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 sm:p-6 bg-gray-50 flex-1">
              <img src={viewModalUrl} alt="Submission screenshot" className="w-full h-auto max-w-3xl mx-auto rounded-lg shadow-sm border border-gray-200" />
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal popup */}
      {confirmModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 60002 }}
          onClick={() => setConfirmModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3 className="font-bold text-gray-900 text-lg">{confirmModal.title}</h3>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">{confirmModal.message}</p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setConfirmModal(null)}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmModal.action) confirmModal.action();
                  else setConfirmModal(null);
                }}
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-colors"
              >
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {rejectModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 60001 }}
          onClick={() => setRejectModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-bold text-gray-900 text-base">Reject Submission</h3>
                <p className="text-xs text-gray-500 mt-0.5">Provide a clear reason so the institute can fix and re-upload.</p>
              </div>
              <button onClick={() => setRejectModal(null)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              rows={4}
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 focus:ring-2 focus:ring-red-400 focus:border-transparent outline-none resize-none shadow-sm"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRejectModal(null)}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReject}
                disabled={!rejectReason.trim() || actionLoadingId === rejectModal.submission_id}
                className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-bold rounded-xl transition-colors"
              >
                {actionLoadingId === rejectModal.submission_id && (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AwardManagementSystem = () => {
  const [categories, setCategories] = useState([]);
  const [nominees, setNominees] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [voteSummary, setVoteSummary] = useState(null);
  const [allAwards, setAllAwards] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editAwardData, setEditAwardData] = useState(null);
  const [questionsAwardData, setQuestionsAwardData] = useState(null);
  const [loading, setLoading] = useState({
    categories: true,
    nominees: false,
    voteSummary: false,
    allAwards: true,
  });
  const [filters, setFilters] = useState({
    institute: "",
    designation: "",
  });
  const [mainTab, setMainTab] = useState("awards");
  const [specialAwardSubTab, setSpecialAwardSubTab] = useState("creation"); // 'creation' or 'nominees'

  const [awardFilters, setAwardFilters] = useState({
    award_type_id: "",
    category_id: "",
    is_active: "",
  });
  const [awardPagination, setAwardPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 0,
    total_records: 0
  });
  const [dropdowns, setDropdowns] = useState({ types: [], categories: [] });
  const [awardSearchTerm, setAwardSearchTerm] = useState("");

  // API utility function with auth token
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem("accessToken");

    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData && errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData && errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Ignore JSON parsing error for non-JSON error responses
      }
      throw new Error(errorMessage);
    }

    return response.json();
  };

  const fetchDropdowns = async () => {
    try {
      const data = await apiCall("/awards/dropdowns/types_category");
      if (data) {
        setDropdowns({
          types: data.types || [],
          categories: data.categories || []
        });
      }
    } catch (error) {
      console.error("Failed to fetch dropdowns:", error);
    }
  };

  // Fetch categories and vote summary on component mount
  useEffect(() => {
    fetchCategories();
    fetchVoteSummary();
    fetchDropdowns();
  }, []);

  useEffect(() => {
    fetchAllAwards();
  }, [awardFilters, awardPagination.page]);

  const fetchAllAwards = async () => {
    try {
      setLoading((prev) => ({ ...prev, allAwards: true }));

      const queryParams = new URLSearchParams();
      if (awardFilters.award_type_id) queryParams.append("award_type_id", awardFilters.award_type_id);
      if (awardFilters.category_id) queryParams.append("category_id", awardFilters.category_id);
      if (awardFilters.is_active !== "") queryParams.append("is_active", awardFilters.is_active);

      queryParams.append("page", awardPagination.page);
      queryParams.append("page_size", awardPagination.page_size);

      const data = await apiCall(`/awards/all?${queryParams.toString()}`);

      if (data && data.data) {
        setAllAwards(data.data);
        setAwardPagination(prev => ({
          ...prev,
          total_pages: data.total_pages,
          total_records: data.total_records
        }));
      } else if (Array.isArray(data)) {
        // Fallback if backend isn't updated yet
        setAllAwards(data);
      }
    } catch (error) {
      console.error("Error fetching all awards:", error);
      // Fallback to empty array if API fails
      setAllAwards([]);
    } finally {
      setLoading((prev) => ({ ...prev, allAwards: false }));
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading((prev) => ({ ...prev, categories: true }));
      const data = await apiCall("/admin/categories");
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      alert("Failed to fetch categories. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, categories: false }));
    }
  };

  const fetchNominees = async (categoryId) => {
    try {
      setLoading((prev) => ({ ...prev, nominees: true }));
      const data = await apiCall(`/admin/nominees/${categoryId}`);
      setNominees(data);
    } catch (error) {
      console.error("Error fetching nominees:", error);
      alert("Failed to fetch nominees. Please try again.");
      setNominees([]);
    } finally {
      setLoading((prev) => ({ ...prev, nominees: false }));
    }
  };

  const fetchVoteSummary = async () => {
    try {
      setLoading((prev) => ({ ...prev, voteSummary: true }));
      const data = await apiCall("/admin/vote-nomination-summary");
      setVoteSummary(data);
    } catch (error) {
      console.error("Error fetching vote summary:", error);
      alert("Failed to fetch vote summary. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, voteSummary: false }));
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedPerson(null);
    setShowDetails(true);
    setNominees([]);
    fetchNominees(category.category_id);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const handleAccept = (person) => {
    alert(
      `${person.name} has been accepted for ${selectedCategory?.category_name}!`
    );
    // Here you can make an API call to update the status
    // await apiCall(`/admin/nominees/${person.nomination_id}/accept`, { method: 'POST' });
  };

  const handleReject = (person) => {
    alert(
      `${person.name} has been rejected for ${selectedCategory?.category_name}.`
    );
    // Here you can make an API call to update the status
    // await apiCall(`/admin/nominees/${person.nomination_id}/reject`, { method: 'POST' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-[1536px] mx-auto">
        {/* Premium Glowing Underline Tabs */}
        <div className="mb-12 flex justify-center relative">
          {/* Subtle background track */}
          <div className="absolute bottom-0 w-full max-w-[600px] h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>

          <div className="flex space-x-16 relative z-10">
            <button
              onClick={() => setMainTab("awards")}
              className="relative pb-5 px-4 transition-all duration-500 group"
            >
              <span className={`text-[13px] font-extrabold tracking-[0.2em] uppercase transition-all duration-500 ${mainTab === "awards"
                ? "bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent"
                : "text-gray-500 group-hover:text-gray-800"
                }`}>
                Awards creations and Nominees
              </span>

              {/* Active Glowing Line */}
              <div className={`absolute bottom-0 left-0 w-full h-[3px] rounded-t-full transition-all duration-500 origin-center ${mainTab === "awards"
                ? "bg-gradient-to-r from-indigo-500 to-purple-500 shadow-[0_-3px_15px_rgba(99,102,241,0.5)] opacity-100 scale-x-100"
                : "bg-transparent opacity-0 scale-x-50"
                }`}></div>
            </button>

            <button
              onClick={() => setMainTab("voteSummary")}
              className="relative pb-5 px-4 transition-all duration-500 group"
            >
              <span className={`text-[13px] font-extrabold tracking-[0.2em] uppercase transition-all duration-500 ${mainTab === "voteSummary"
                ? "bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent"
                : "text-gray-500 group-hover:text-gray-800"
                }`}>
                Vote & Nomination Summary
              </span>
              
              {/* Active Glowing Line */}
              <div className={`absolute bottom-0 left-0 w-full h-[3px] rounded-t-full transition-all duration-500 origin-center ${mainTab === "voteSummary"
                ? "bg-gradient-to-r from-indigo-500 to-purple-500 shadow-[0_-3px_15px_rgba(99,102,241,0.5)] opacity-100 scale-x-100"
                : "bg-transparent opacity-0 scale-x-50"
                }`}></div>
            </button>
            
            <button
              onClick={() => setMainTab("milestone")}
              className="relative pb-5 px-4 transition-all duration-500 group"
            >
              <span className={`text-[13px] font-extrabold tracking-[0.2em] uppercase transition-all duration-500 ${mainTab === "milestone"
                ? "bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent"
                : "text-gray-500 group-hover:text-gray-800"
                }`}>
                1M Internship Milestone
              </span>
              <div className={`absolute bottom-0 left-0 w-full h-[3px] rounded-t-full transition-all duration-500 origin-center ${mainTab === "milestone"
                ? "bg-gradient-to-r from-indigo-500 to-purple-500 shadow-[0_-3px_15px_rgba(99,102,241,0.5)] opacity-100 scale-x-100"
                : "bg-transparent opacity-0 scale-x-50"
                }`}></div>
            </button>
          </div>
        </div>

{mainTab === "milestone" ? (
          <MilestoneSubmissions />
        ) : mainTab === "voteSummary" ? (          <>
            <VoteSummary
              voteSummary={voteSummary}
              loading={loading.voteSummary}
              apiCall={apiCall}
            />
            <CompactCategorySelector
              onCategorySelect={handleCategorySelect}
              selectedCategory={selectedCategory}
              categories={categories}
              loading={loading.categories}
            />

            {showDetails && (
              <ActionButtons
                selectedPerson={selectedPerson}
                selectedCategory={selectedCategory}
                onAccept={handleAccept}
                onReject={handleReject}
                apiCall={apiCall}
              />
            )}

            {showDetails && (
              <div className="flex gap-6 overflow-hidden">
                <FilterSidebar
                  data={nominees}
                  onSelectPerson={setSelectedPerson}
                  selectedPerson={selectedPerson}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  isVisible={showDetails}
                  loading={loading.nominees}
                />

                <div className="flex-1">
                  <DetailedAccordion
                    person={selectedPerson}
                    isVisible={showDetails}
                    apiCall={apiCall}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-6 relative">
            {/* Sub-navigation for Special Category Awards */}
            <div className="flex overflow-x-auto space-x-2 border-b border-gray-200 mb-6 pb-0 scrollbar-hide">
             <button
                onClick={() => setSpecialAwardSubTab("creation")}
                className={`px-4 py-2.5 text-sm font-bold transition-colors whitespace-nowrap border-b-2 ${specialAwardSubTab === "creation"
                  ? 'text-indigo-700 border-indigo-500'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Award Creation
              </button>
           <button
                onClick={() => setSpecialAwardSubTab("nominees")}
                className={`px-4 py-2.5 text-sm font-bold transition-colors whitespace-nowrap border-b-2 ${specialAwardSubTab === "nominees"
                  ? 'text-indigo-700 border-indigo-500'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Nominees
              </button>
            </div>

            {specialAwardSubTab === "creation" ? (
              <>
                {/* Header / Action Bar */}
            <div className="flex justify-between items-center mb-6 px-2">
              <h2 className="text-2xl font-bold text-gray-800">Available Awards</h2>
              <button
                onClick={() => { setEditAwardData(null); setIsAddModalOpen(true); }}
                className="flex items-center px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl shadow-md transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Award
              </button>
            </div>

            {/* Filter Bar & Search */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 px-2 gap-4">
              {/* Search Input */}
              <div className="relative w-full md:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search awards..."
                  value={awardSearchTerm}
                  onChange={(e) => {
                    setAwardSearchTerm(e.target.value);
                  }}
                  className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm text-gray-700 bg-white shadow-sm transition-all placeholder:text-gray-400"
                />
                {awardSearchTerm && (
                  <button
                    onClick={() => setAwardSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100 inline-flex flex-wrap gap-3 items-center w-full md:w-auto justify-end">

                <select
                  value={awardFilters.award_type_id}
                  onChange={(e) => {
                    setAwardFilters(prev => ({ ...prev, award_type_id: e.target.value }));
                    setAwardPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700 bg-gray-50/50 min-w-[140px]"
                >
                  <option value="">All Award Types</option>
                  {dropdowns.types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>

                <select
                  value={awardFilters.category_id}
                  onChange={(e) => {
                    setAwardFilters(prev => ({ ...prev, category_id: e.target.value }));
                    setAwardPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700 bg-gray-50/50 min-w-[140px]"
                >
                  <option value="">All Categories</option>
                  {dropdowns.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>

                <select
                  value={awardFilters.is_active}
                  onChange={(e) => {
                    setAwardFilters(prev => ({ ...prev, is_active: e.target.value }));
                    setAwardPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700 bg-gray-50/50 min-w-[120px]"
                >
                  <option value="">All Statuses</option>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>

                {(awardFilters.award_type_id || awardFilters.category_id || awardFilters.is_active !== "" || awardSearchTerm) && (
                  <button
                    onClick={() => {
                      setAwardFilters({ award_type_id: "", category_id: "", is_active: "" });
                      setAwardSearchTerm("");
                      setAwardPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="px-3 py-2 text-sm text-red-500 hover:text-red-700 font-bold transition-colors"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>

            {loading.allAwards ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white/50 rounded-2xl border border-white">
                <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 font-medium">Loading awards...</p>
              </div>
            ) : (() => {
              const filteredAwards = awardSearchTerm.trim()
                ? allAwards.filter(award => award.award_name?.toLowerCase().includes(awardSearchTerm.trim().toLowerCase()))
                : allAwards;
              return filteredAwards && filteredAwards.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredAwards.map((award) => (
                  <div 
                    key={award.id} 
                    onClick={() => setQuestionsAwardData(award)}
                    className="relative cursor-pointer bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-500 border border-gray-100 overflow-hidden flex flex-col h-full group hover:-translate-y-1 hover:ring-2 hover:ring-indigo-100"
                  >
                    {/* Top Right Actions & Category Badge */}
                    <div className="absolute top-0 right-0 z-10 flex items-start">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditAwardData(award); setIsAddModalOpen(true); }}
                        className=" mr-2 p-1.5 bg-white/80 backdrop-blur-sm text-gray-500 hover:text-indigo-600 hover:bg-white rounded-lg transition-all"
                        title="Edit Award"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>

                      {award.category && (
                        <div className="bg-gradient-to-bl from-gray-900 to-gray-800 text-[10px] uppercase tracking-[0.15em] font-extrabold px-5 py-2 rounded-bl-3xl shadow-lg border-b border-l border-white/10 flex items-center group/badge hover:from-gray-800 hover:to-gray-700 transition-colors">
                          <Star className="w-3 h-3 mr-1.5 text-amber-400 fill-amber-400 drop-shadow-[0_0_2px_rgba(251,191,36,0.5)]" />
                          <span className="bg-gradient-to-r from-amber-200 to-yellow-500 bg-clip-text text-transparent">
                            {award.category}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Header */}
                    <div className="p-6 pb-4 pt-7">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex items-start space-x-4 w-full">
                          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center border border-indigo-100 shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                            <AwardIcon className="w-6 h-6 text-indigo-600 drop-shadow-sm" />
                          </div>
                          <div className="flex-grow min-w-0">
                            <h3 className="text-lg font-extrabold text-gray-800 line-clamp-2 leading-tight group-hover:text-indigo-600 transition-colors" title={award.award_name}>
                              {award.award_name}
                            </h3>
                            <div className="flex items-center mt-2 space-x-2">
                              {award.is_active === "1" ? (
                                <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100/80 px-2 py-1 rounded-md">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-bold text-rose-700 bg-rose-100/80 px-2 py-1 rounded-md">
                                  <XCircle className="w-3 h-3 mr-1" /> Inactive
                                </span>
                              )}
                              {award.sort_order !== null && award.sort_order !== undefined && (
                                <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-bold text-blue-700 bg-blue-100/80 px-2 py-1 rounded-md">
                                  <span className="font-extrabold mr-1">#</span> Sort: {award.sort_order}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Content (Criteria & Badges) */}
                    <div className="px-6 py-4 space-y-4 flex-grow flex flex-col">
                      {award.criteria && (
                        <div className="text-[13px] text-gray-500 line-clamp-3 leading-relaxed mb-1" title={award.criteria}>
                          <span className="font-semibold text-gray-600 mr-1">Criteria:</span>
                          {award.criteria}
                        </div>
                      )}

                      {award.nomination_process && (
                        <div className="text-[13px] text-gray-500 line-clamp-3 leading-relaxed mb-1" title={award.nomination_process}>
                          <span className="font-semibold text-gray-600 mr-1">Nomination Process:</span>
                          {award.nomination_process}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 mt-auto">
                        {award.award_type && (
                          <div className="inline-flex items-center px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-100 text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors cursor-default" title="Award Type">
                            <Users className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                            <span className="capitalize">{award.award_type}</span>
                          </div>
                        )}
                        <div className="inline-flex items-center px-2.5 py-1.5 rounded-lg bg-blue-50/50 border border-blue-100 text-xs font-bold text-blue-700 cursor-default" title="Nomination Fee">
                          <Banknote className="w-3.5 h-3.5 mr-1.5 text-blue-500" />
                          {award.nomination_fee > 0 ? `₹${award.nomination_fee}` : "Free"}
                          {award.nomination_fee > 0 && award.gst_percentage > 0 && <span className="opacity-60 ml-1 font-medium">+{award.gst_percentage}% GST</span>}
                        </div>
                        {award.prize && (
                          <div className="inline-flex items-center px-2.5 py-1.5 rounded-lg bg-purple-50/50 border border-purple-100 text-xs font-bold text-purple-700 cursor-default" title="Prize">
                            <Trophy className="w-3.5 h-3.5 mr-1.5 text-purple-500" />
                            {award.prize}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer Dates */}
                    {(award.nomination_start_date || award.nomination_end_date || award.voting_start_date || award.voting_end_date || award.winner_date || award.winner_pop_up_end) && (
                      <div className="bg-gray-50/50 p-6 pt-5 mt-4 border-t border-gray-100">
                        <div className="space-y-3.5">
                          {(award.nomination_start_date || award.nomination_end_date) && (
                            <div className="flex items-center justify-between group/date">
                              <div className="flex items-center text-xs text-gray-500">
                                <Calendar className="w-4 h-4 mr-2 text-indigo-400 group-hover/date:text-indigo-600 transition-colors" />
                                <span className="font-semibold uppercase tracking-wider text-[10px]">Nomination</span>
                              </div>
                              <span className="text-xs font-bold text-gray-700 bg-white px-2.5 py-1 rounded-md border border-gray-200 shadow-sm">
                                {award.nomination_start_date ? new Date(award.nomination_start_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : "TBD"} - {award.nomination_end_date ? new Date(award.nomination_end_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : "TBD"}
                              </span>
                            </div>
                          )}

                          {(award.voting_start_date || award.voting_end_date) && (
                            <div className="flex items-center justify-between group/date">
                              <div className="flex items-center text-xs text-gray-500">
                                <Star className="w-4 h-4 mr-2 text-amber-400 group-hover/date:text-amber-500 transition-colors" />
                                <span className="font-semibold uppercase tracking-wider text-[10px]">Voting</span>
                              </div>
                              <span className="text-xs font-bold text-gray-700 bg-white px-2.5 py-1 rounded-md border border-gray-200 shadow-sm">
                                {award.voting_start_date ? new Date(award.voting_start_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : "TBD"} - {award.voting_end_date ? new Date(award.voting_end_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : "TBD"}
                              </span>
                            </div>
                          )}

                          {(award.winner_date || award.winner_pop_up_end) && (
                            <div className="flex items-center justify-between group/date">
                              <div className="flex items-center text-xs text-gray-500">
                                <AwardIcon className="w-4 h-4 mr-2 text-rose-400 group-hover/date:text-rose-600 transition-colors" />
                                <span className="font-semibold uppercase tracking-wider text-[10px]">Winner</span>
                              </div>
                              <span className="text-xs font-bold text-gray-700 bg-white px-2.5 py-1 rounded-md border border-gray-200 shadow-sm">
                                {award.winner_date ? new Date(award.winner_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : "TBD"} - {award.winner_pop_up_end ? new Date(award.winner_pop_up_end).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : "TBD"}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm p-16 text-center text-gray-500 border border-gray-100">
                <div className="text-5xl mb-4 opacity-50">🏆</div>
                <h3 className="text-xl font-bold mb-2 text-gray-700">No Awards Found</h3>
                <p className="text-gray-500">{awardSearchTerm ? `No awards matching "${awardSearchTerm}"` : "There are currently no awards available to display."}</p>
              </div>
            )
            })()}

            {/* Pagination Controls */}
            {!loading.allAwards && allAwards && allAwards.length > 0 && awardPagination.total_pages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-4 mt-8 pt-6 border-t border-gray-200">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Rows per page:</span>
                        <select
                          value={awardPagination.page_size}
                          onChange={(e) => {
                            setAwardPagination((prev) => ({
                              ...prev,
                              page_size: Number(e.target.value),
                              page: 1,
                            }));
                          }}
                          className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 bg-white focus:ring-2 focus:ring-indigo-400 outline-none transition-all shadow-2xs cursor-pointer"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </select>
                      </div>
                      <span className="text-gray-300 hidden sm:inline">|</span>
                      <div className="text-sm text-gray-600 font-medium">
                        Showing <span className="font-bold text-gray-900">{((awardPagination.page - 1) * awardPagination.page_size) + 1}</span> to <span className="font-bold text-gray-900">{Math.min(awardPagination.page * awardPagination.page_size, awardPagination.total_records)}</span> of <span className="font-bold text-gray-900">{awardPagination.total_records}</span> awards
                      </div>
                    </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setAwardPagination(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
                    disabled={awardPagination.page === 1}
                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="flex space-x-1">
                    {[...Array(awardPagination.total_pages)].map((_, i) => {
                      const pageNum = i + 1;
                      if (pageNum === 1 || pageNum === awardPagination.total_pages || Math.abs(awardPagination.page - pageNum) <= 1) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setAwardPagination(prev => ({ ...prev, page: pageNum }))}
                            className={`px-3.5 py-1.5 rounded-lg text-sm font-bold transition-colors ${awardPagination.page === pageNum
                              ? "bg-indigo-600 text-white shadow-md"
                              : "text-gray-600 hover:bg-gray-100"
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (Math.abs(awardPagination.page - pageNum) === 2) {
                        return <span key={`dots-${pageNum}`} className="px-2 text-gray-400">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => setAwardPagination(prev => ({ ...prev, page: Math.min(prev.page + 1, awardPagination.total_pages) }))}
                    disabled={awardPagination.page === awardPagination.total_pages}
                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
            </>
            ) : (
              <NomineesDashboard apiCall={apiCall} />
            )}
          </div>
        )}

        {/* Add Award Modal */}
        <AddAwardModal
          isOpen={isAddModalOpen}
          onClose={() => { setIsAddModalOpen(false); setEditAwardData(null); }}
          apiCall={apiCall}
          onAwardAdded={fetchAllAwards}
          editData={editAwardData}
        />

        {/* Award Questions Modal */}
        <AwardQuestionsModal
          isOpen={!!questionsAwardData}
          onClose={() => setQuestionsAwardData(null)}
          apiCall={apiCall}
          award={questionsAwardData}
        />
      </div>
    </div>
  );
};

export default AwardManagementSystem;
